import { hoverStyle } from "@/styles/theme";
import type { ListItemProps } from "@atoms/ListItem";
import { ListItem } from "@atoms/ListItem";
import { HEADER } from "@modules/orders/constants/Size";
import { Box, IconButton, List, Stack, Tooltip } from "@mui/material";
import { UserAccountBadge } from "@organisms/UserAccountBadge";
import { Images } from "@shared/constants/Images";
import type { SideMenuItems } from "@shared/constants/SideMenuConstants";
import {
  MenuItems,
  isAccountManagement,
  isBillings,
  isInternal,
} from "@shared/constants/SideMenuConstants";
import {
  useSideMenu,
  useSideMenu as useSideMenuContext,
} from "@shared/contexts/SideMenuContext";
import { useUserAccount } from "@shared/contexts/UserAccountContext";
import { useEffect, useState } from "react";

const TOOLTIP_TOGGLE_TEXT_CLOSE = "閉じる";
const TOOLTIP_TOGGLE_TEXT_OPEN = "開く";

type TooltipToggleText =
  | typeof TOOLTIP_TOGGLE_TEXT_CLOSE
  | typeof TOOLTIP_TOGGLE_TEXT_OPEN;

const SideMenuContentSection: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <List
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: `calc(100vh - ${HEADER.height} - 32px)`,
      overflowY: "auto",
    }}
  >
    {children}
  </List>
);

const closeContent = (
  toggleSideMenu: () => void,
  tooltipToggleText: string,
  isOpenSideMenu: boolean,
) => {
  const closeIcon = (closeIcon: React.ReactElement) => {
    if (isOpenSideMenu) return closeIcon;
    return (
      <Tooltip title={tooltipToggleText} placement="right">
        {closeIcon}
      </Tooltip>
    );
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        mt: 2,
        ...hoverStyle,
      }}
      onClick={toggleSideMenu}
    >
      {closeIcon(
        <IconButton
          aria-label={tooltipToggleText}
          sx={{
            bgcolor: (theme) => theme.palette.grey[200],
            "&:hover": {
              bgcolor: (theme) => theme.palette.grey[300],
            },
          }}
        >
          <img
            src={Images.icons.common.close}
            alt={tooltipToggleText}
            width={24}
            height={24}
            style={{
              transform: isOpenSideMenu ? "none" : "scaleX(-1)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>,
      )}
      {isOpenSideMenu && <span>{tooltipToggleText}</span>}
    </Box>
  );
};

export const SideMenuContent: React.FC = () => {
  const userAccount = useUserAccount();
  const { selectedItem, setSelectedItem } = useSideMenuContext();
  const { isOpenSideMenu, toggleSideMenu } = useSideMenu();
  const [tooltipToggleText, setTooltipToggleText] = useState<TooltipToggleText>(
    TOOLTIP_TOGGLE_TEXT_CLOSE,
  );

  useEffect(() => {
    setTooltipToggleText(
      isOpenSideMenu ? TOOLTIP_TOGGLE_TEXT_CLOSE : TOOLTIP_TOGGLE_TEXT_OPEN,
    );
  }, [isOpenSideMenu]);

  const renderMenuItems = (items: ListItemProps[], isExternal = false) =>
    items.map((item) => {
      // 一般ユーザーはアカウント管理メニューを表示しない
      if (isAccountManagement(item.text) && userAccount.isGeneral()) {
        return;
      }
      // 上位グループS0001以外は社内業務メニューを表示しない
      if (isInternal(item.text) && !userAccount.isInternal()) {
        return;
      }
      // 上位グループS0001以外は注文伝票管理メニューを表示しない
      if (isBillings(item.text) && !userAccount.isInternal()) {
        return;
      }
      return (
        <ListItem
          key={item.text}
          text={item.text}
          iconPath={item.iconPath}
          href={item.href}
          alt={item.alt}
          external={isExternal ? item.external : null}
          isSelected={selectedItem === item.text}
          onClick={(e) => {
            setSelectedItem(item.text as SideMenuItems);
            item.onClick?.(e);
          }}
          showFull={isOpenSideMenu}
        />
      );
    });

  const content = (
    <>
      <Box>{renderMenuItems(MenuItems.internal, false)}</Box>

      <Stack>
        {renderMenuItems(MenuItems.external, true)}
        <UserAccountBadge />
        {closeContent(toggleSideMenu, tooltipToggleText, isOpenSideMenu)}
      </Stack>
    </>
  );

  return <SideMenuContentSection> {content} </SideMenuContentSection>;
};
